import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FREE_LIMITS } from "@/lib/premiumConstants";
import { setCachedData, getCachedData } from "@/lib/offlineCache";
import { fetchPremiumAccess } from "@/lib/premiumStatus";

// Re-export for backwards compatibility
export { FREE_LIMITS } from "@/lib/premiumConstants";

export interface PremiumStatus {
  isPremium: boolean;
  isCoach: boolean;
  hasCoachAccess: boolean;
  isLoading: boolean;
  // AI workout usage tracking
  aiWorkoutsUsedThisWeek: number;
  canGenerateAIWorkout: boolean;
  remainingAIWorkouts: number;
  // Saved items counts
  savedWorkoutsCount: number;
  savedExercisesCount: number;
  canSaveWorkout: boolean;
  canSaveExercise: boolean;
  // AI refinement tracking
  aiRefinementsUsedThisWeek: number;
  canRefineWorkout: boolean;
  remainingRefinements: number;
  incrementRefinementUsage: () => Promise<void>;
  // Custom exercises
  customExercisesCount: number;
  canCreateCustomExercise: boolean;
  // Progress photos
  progressPhotosCount: number;
  canUploadPhoto: boolean;
  // Actions
  incrementAIUsage: () => Promise<void>;
  checkPremiumStatus: () => Promise<boolean>;
  refreshUsage: () => Promise<void>;
}

export function usePremiumStatus(): PremiumStatus {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [aiWorkoutsUsedThisWeek, setAiWorkoutsUsedThisWeek] = useState(0);
  const [aiRefinementsUsedThisWeek, setAiRefinementsUsedThisWeek] = useState(0);
  const [savedWorkoutsCount, setSavedWorkoutsCount] = useState(0);
  const [savedExercisesCount, setSavedExercisesCount] = useState(0);
  const [customExercisesCount, setCustomExercisesCount] = useState(0);
  const [progressPhotosCount, setProgressPhotosCount] = useState(0);

  const [isBypassUser, setIsBypassUser] = useState(false);

  const checkPremiumStatus = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    if (!navigator.onLine) {
      const cached = getCachedData<{ isPremium: boolean; isBypass: boolean; isCoach?: boolean }>(user.id, "premium");
      if (cached) {
        setIsBypassUser(cached.isBypass);
        setIsPremium(cached.isBypass || cached.isPremium);
        setIsCoach(!!cached.isCoach || cached.isBypass);
        return cached.isBypass || cached.isPremium;
      }
      return false;
    }

    const { isPremium: premium, isBypass: bypass, isCoach: coach } = await fetchPremiumAccess(user.id);
    setIsBypassUser(bypass);
    setIsPremium(premium);
    setIsCoach(coach);

    setCachedData(user.id, "premium", { isPremium: premium, isBypass: bypass, isCoach: coach });

    return premium;
  }, [user]);

  const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(now.setDate(diff));
    weekStart.setHours(0, 0, 0, 0);
    return weekStart.toISOString().split('T')[0];
  };

  const fetchAIUsage = useCallback(async () => {
    if (!user) return;
    const weekStart = getWeekStart();

    const [workoutUsage, refinementUsage] = await Promise.all([
      supabase
        .from("ai_usage_tracking")
        .select("usage_count")
        .eq("user_id", user.id)
        .eq("usage_type", "ai_workout")
        .eq("week_start", weekStart)
        .maybeSingle(),
      supabase
        .from("ai_usage_tracking")
        .select("usage_count")
        .eq("user_id", user.id)
        .eq("usage_type", "ai_refinement")
        .eq("week_start", weekStart)
        .maybeSingle(),
    ]);

    setAiWorkoutsUsedThisWeek(workoutUsage.data?.usage_count || 0);
    setAiRefinementsUsedThisWeek(refinementUsage.data?.usage_count || 0);
  }, [user]);

  const fetchSavedCounts = useCallback(async () => {
    if (!user) return;

    const [workoutsRes, exercisesRes, customRes] = await Promise.all([
      supabase
        .from("saved_workouts")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("favorite_exercises")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("custom_exercises")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id),
    ]);

    setSavedWorkoutsCount(workoutsRes.count || 0);
    setSavedExercisesCount(exercisesRes.count || 0);
    setCustomExercisesCount(customRes.count || 0);
  }, [user]);

  const fetchPhotosCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data: files } = await supabase.storage
        .from("progress-photos")
        .list(user.id);
      const count = (files || []).filter(f => f.name !== ".emptyFolderPlaceholder" && !f.name.includes("avatar")).length;
      setProgressPhotosCount(count);
    } catch {
      setProgressPhotosCount(0);
    }
  }, [user]);

  const incrementAIUsage = useCallback(async () => {
    if (!user) return;
    if (isBypassUser || isPremium) return;

    const weekStart = getWeekStart();
    const { data: existing } = await supabase
      .from("ai_usage_tracking")
      .select("id, usage_count")
      .eq("user_id", user.id)
      .eq("usage_type", "ai_workout")
      .eq("week_start", weekStart)
      .maybeSingle();

    if (existing) {
      await supabase
        .from("ai_usage_tracking")
        .update({ usage_count: existing.usage_count + 1 })
        .eq("id", existing.id);
    } else {
      await supabase
        .from("ai_usage_tracking")
        .insert({
          user_id: user.id,
          usage_type: "ai_workout",
          week_start: weekStart,
          usage_count: 1,
        });
    }

    setAiWorkoutsUsedThisWeek(prev => prev + 1);
  }, [user, isBypassUser, isPremium]);

  const incrementRefinementUsage = useCallback(async () => {
    if (!user) return;
    if (isBypassUser || isPremium) return;

    const weekStart = getWeekStart();
    const { data: existing } = await supabase
      .from("ai_usage_tracking")
      .select("id, usage_count")
      .eq("user_id", user.id)
      .eq("usage_type", "ai_refinement")
      .eq("week_start", weekStart)
      .maybeSingle();

    if (existing) {
      await supabase
        .from("ai_usage_tracking")
        .update({ usage_count: existing.usage_count + 1 })
        .eq("id", existing.id);
    } else {
      await supabase
        .from("ai_usage_tracking")
        .insert({
          user_id: user.id,
          usage_type: "ai_refinement",
          week_start: weekStart,
          usage_count: 1,
        });
    }

    setAiRefinementsUsedThisWeek(prev => prev + 1);
  }, [user, isBypassUser, isPremium]);

  const refreshUsage = useCallback(async () => {
    await Promise.all([
      fetchAIUsage(),
      fetchSavedCounts(),
      fetchPhotosCount(),
    ]);
  }, [fetchAIUsage, fetchSavedCounts, fetchPhotosCount]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([
        checkPremiumStatus(),
        fetchAIUsage(),
        fetchSavedCounts(),
        fetchPhotosCount(),
      ]);
      setIsLoading(false);
    };

    if (user) {
      init();
    } else {
      setIsLoading(false);
    }
  }, [user, checkPremiumStatus, fetchAIUsage, fetchSavedCounts, fetchPhotosCount]);

  // Re-check premium status when trial is granted elsewhere in the app
  useEffect(() => {
    if (!user) return;
    const handler = () => { void checkPremiumStatus(); };
    window.addEventListener("premium-status-changed", handler);
    return () => window.removeEventListener("premium-status-changed", handler);
  }, [user, checkPremiumStatus]);

  const effectivelyPremium = isBypassUser || isPremium;
  
  const canGenerateAIWorkout = effectivelyPremium || aiWorkoutsUsedThisWeek < FREE_LIMITS.AI_WORKOUTS_PER_WEEK;
  const remainingAIWorkouts = effectivelyPremium 
    ? Infinity 
    : Math.max(0, FREE_LIMITS.AI_WORKOUTS_PER_WEEK - aiWorkoutsUsedThisWeek);

  const canSaveWorkout = effectivelyPremium || savedWorkoutsCount < FREE_LIMITS.MAX_SAVED_WORKOUTS;
  const canSaveExercise = effectivelyPremium || savedExercisesCount < FREE_LIMITS.MAX_SAVED_EXERCISES;

  const canRefineWorkout = effectivelyPremium || aiRefinementsUsedThisWeek < FREE_LIMITS.AI_REFINEMENTS_PER_WEEK;
  const remainingRefinements = effectivelyPremium
    ? Infinity
    : Math.max(0, FREE_LIMITS.AI_REFINEMENTS_PER_WEEK - aiRefinementsUsedThisWeek);

  const canCreateCustomExercise = effectivelyPremium || customExercisesCount < FREE_LIMITS.MAX_CUSTOM_EXERCISES;
  const canUploadPhoto = effectivelyPremium || progressPhotosCount < FREE_LIMITS.MAX_PROGRESS_PHOTOS;

  return {
    isPremium: effectivelyPremium,
    isCoach,
    hasCoachAccess: isBypassUser || isCoach,
    isLoading,
    aiWorkoutsUsedThisWeek,
    canGenerateAIWorkout,
    remainingAIWorkouts,
    savedWorkoutsCount,
    savedExercisesCount,
    canSaveWorkout,
    canSaveExercise,
    aiRefinementsUsedThisWeek,
    canRefineWorkout,
    remainingRefinements,
    incrementRefinementUsage,
    customExercisesCount,
    canCreateCustomExercise,
    progressPhotosCount,
    canUploadPhoto,
    incrementAIUsage,
    checkPremiumStatus,
    refreshUsage,
  };
}
